import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import setAddressBookOverlayIsOpen from "@insite/client-framework/Store/Components/AddressBookOverlay/Handlers/SetAddressBookOverlayIsOpen";
import { getCurrentUserIsGuest, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import setUseBillingAddress from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/SetUseBillingAddress";
import setUseOneTimeAddress from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/SetUseOneTimeAddress";
import translate from "@insite/client-framework/Translate";
import { AddressFieldDisplayCollectionModel, BaseAddressModel, CountryModel, ShipToModel } from "@insite/client-framework/Types/ApiModels";
import { AddressInfoDisplayStyles } from "@insite/content-library/Components/AddressInfoDisplay";
import ShipToSelector, { ShipToSelectorStyles } from "@insite/content-library/Components/ShipToSelector";
import CreateNewAddressModal, { CreateNewAddressModalStyles } from "@insite/content-library/Widgets/CheckoutShipping/CreateNewAddressModal";
import EditExistingAddressModal, { EditExistingAddressModalStyles } from "@insite/content-library/Widgets/CheckoutShipping/EditExistingAddressModal";
import ShippingAddressForm from "@insite/content-library/Widgets/CheckoutShipping/ShippingAddressForm";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Radio from "@insite/mobius/Radio";
import RadioGroup, { RadioGroupProps } from "@insite/mobius/RadioGroup";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import {
    getSelectedShippingAddressType,
    SelectedShippingAddressType,
} from "@tSurya/Common/Utilities/checkoutShippingValidation";
import validateSelectedShippingAddress from "@tSurya/Handlers/CheckoutShipping/ValidateSelectedShippingAddress";
import AddressInfoDisplay from "@tSurya/Overrides/Components/AddressInfoDisplay";
import CheckoutSectionHeader from "@tSurya/Components/CheckoutSectionHeader";
import RadioCard from "@tSurya/Components/RadioCard";
import CustomApplicationState from "@tSurya/Store/Data/CustomApplicationState/CustomApplicationState";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import React, { Dispatch, FC, PropsWithChildren, useContext, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { AnyAction } from "redux";
import { css, ThemeProps, withTheme } from "styled-components";
import { COLORS, TYPOGRAPHY } from "@tSurya/theme/design.surya";
import { breakpointMediaQueries } from "@insite/mobius/utilities";

interface OwnProps {
    address?: ShipToModel;
    currentBillToId: string | undefined;
    newAddress?: ShipToModel;
    oneTimeAddress?: ShipToModel;
    countries: CountryModel[];
    addressFieldDisplayCollection: AddressFieldDisplayCollectionModel;
    onChange: (address: ShipToModel, isEdit: boolean) => void;
    showUseBillingAddress?: boolean;
    isUseBillingAddressDisabled?: boolean;
    onClickUseBillingAddress?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
    extendedStyles?: ShippingAddressStyles;
}

const mapStateToProps = (state: CustomApplicationState) => {
    return {
        currentUserIsGuest: getCurrentUserIsGuest(state),
        useBillingAddress: state.pages.checkoutShipping.useBillingAddress,
        useOneTimeAddress: state.pages.checkoutShipping.useOneTimeAddress,
        allowCreateNewShipToAddress: getSettingsCollection(state).customerSettings.allowCreateNewShipToAddress,
        shippingAddressFormState: state.pages.checkoutShipping.shippingAddressFormState,
        isShippingAddressUpdateRequired: state.pages.checkoutShipping.isShippingAddressUpdateRequired,
        reduxSelectedAddressType: getSelectedShippingAddressType(state),
        isUpdatingCustomer: state.pages.checkoutShipping.isUpdatingCustomer,
        isUpdatingCart: state.pages.checkoutShipping.isUpdatingCart,
    };
};

const mapDispatchToProps = {
    setUseBillingAddress,
    setUseOneTimeAddress,
    setAddressBookOverlayIsOpen,
    validateSelectedShippingAddress,
    markShippingAddressOptionSelected: () => (dispatch: Dispatch<AnyAction>) => {
        dispatch({
            type: "Custom/CheckoutShipping/SetShippingAddressOptionSelected",
            isSelected: true,
        });
    },
    setAddress: (address: ShipToModel) => (dispatch: Dispatch<AnyAction>) => {
        dispatch({
            type: "Pages/CheckoutShipping/SetShippingAddress",
            address,
        });
    },
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & ThemeProps<BaseTheme>;

export interface ShippingAddressStyles {
    container?: GridContainerProps;
    cardContentGridItem?: GridItemProps;
    cardContent?: InjectableCss;
    addressTypeRadioGroup?: RadioGroupProps;
    addressBookModal?: ModalPresentationProps;
    shipToSelector?: ShipToSelectorStyles;
    addressBookModalButtonsWrapper?: InjectableCss;
    addressBookModalCancelButton?: ButtonPresentationProps;
    createNewAddressModal?: CreateNewAddressModalStyles;
    editAddressModal?: EditExistingAddressModalStyles;
    oneTimeAddressGridItem?: GridItemProps;
    addressDisplayAndFormGridItem?: GridItemProps;
    addressDisplay?: AddressInfoDisplayStyles;
    oneTimeAddressWrapper?: InjectableCss;
    billingAddressWrapper?: InjectableCss;
    loadingOverlay?: InjectableCss;
}

export const shippingAddressStyles: ShippingAddressStyles = {
    container: {
        gap: 0,
        css: css`
            width: 100%;
        `,
    },
    cardContentGridItem: {
        width: 12,
        css: css`
            display: block;
        `,
    },
    addressTypeRadioGroup: {
        css: css`
            label {
                font-size: 16px;
                font-weight: 700;
            }

            & > [class^="RadioStyle"] + span:empty {
                display: none !important;
                margin: 0 !important;
                padding: 0 !important;
            }

            & > [class^="RadioStyle"] {
                margin-bottom: 12px;
            }

            & > [class^="RadioStyle"]:last-of-type {
                margin-bottom: 0 !important;
            }
        `,
    },
    cardContent: {
        css: css`
            background-color: ${COLORS.white};
            border: 1px solid ${COLORS.border.main};
            border-radius: 4px;
            padding: 20px;

            .address-option {
                margin-bottom: 16px;
            }

            .address-option label,
            .address-option input {
                cursor: pointer;
            }
        `,
    },
    addressBookModal: {
        sizeVariant: "medium",
        cssOverrides: {
            modalTitle: css`
                border: 0px;
                padding: 10px 20px;
                background-color: #f8f8f8;
                font-family: ${TYPOGRAPHY.fontFamily.title};
                font-weight: ${TYPOGRAPHY.weight.bold};
                font-size: ${TYPOGRAPHY.size.larger};
                line-height: ${TYPOGRAPHY.lineHeight.lmedium};
                font-style: normal;

                h1,
                h2,
                h3,
                h4,
                h5,
                h6 {
                    font-family: ${TYPOGRAPHY.fontFamily.title} !important;
                    font-weight: ${TYPOGRAPHY.weight.bold} !important;
                    font-size: ${TYPOGRAPHY.size.larger} !important;
                    line-height: ${TYPOGRAPHY.lineHeight.lmedium} !important;
                    font-style: normal;
                }
            `,
        },
    },
    shipToSelector: {
        noAddressesText: {
            variant: "p",
            css: css`
                font-family: ${TYPOGRAPHY.fontFamily.title} !important;
                font-style: normal;
                text-align: center;
            `,
        },
    },
    addressBookModalButtonsWrapper: {
        css: css`
            display: flex;
            justify-content: flex-end;
            margin-top: 1rem;
        `,
    },
    addressBookModalCancelButton: { variant: "secondary" },
    oneTimeAddressGridItem: { width: 12 },
    addressDisplayAndFormGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
        `,
    },
    oneTimeAddressWrapper: {
        css: css`
            margin-top: 16px;
        `,
    },
    billingAddressWrapper: {
        css: css`
            margin-top: 16px;
        `,
    },
    loadingOverlay: {
        css: css`
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 20px;
            margin-left: 30px;
            margin-top: 10px;
        `,
    },
};

const ShippingAddress: FC<PropsWithChildren<Props>> = ({
    address,
    newAddress,
    oneTimeAddress,
    onChange,
    currentUserIsGuest,
    useBillingAddress,
    useOneTimeAddress,
    allowCreateNewShipToAddress,
    setUseBillingAddress,
    setUseOneTimeAddress,
    extendedStyles,
    currentBillToId,
    shippingAddressFormState,
    showUseBillingAddress,
    isShippingAddressUpdateRequired,
    countries,
    addressFieldDisplayCollection,
    theme,
    setAddressBookOverlayIsOpen,
    reduxSelectedAddressType,
    isUpdatingCustomer,
    isUpdatingCart,
    validateSelectedShippingAddress,
    setAddress,
    markShippingAddressOptionSelected,
}) => {
    const [isCustomerSelectorModalOpen, setIsCustomerSelectorModalOpen] = useState(false);
    const [isCreateNewAddressModalOpen, setIsCreateNewAddressModalOpen] = useState(false);
    const [isEditAddressModalOpen, setIsEditAddressModalOpen] = useState(false);
    const [addressBeingEdited, setAddressBeingEdited] = useState<ShipToModel | undefined>(undefined);
    const toasterContext = useContext(ToasterContext);
    const [styles] = useState(() => mergeToNew(shippingAddressStyles, extendedStyles));

    // Optimistic override so the radio highlights immediately while the async
    // set-address handler chains persist the choice to Redux/cart.
    const [pendingAddressType, setPendingAddressType] = useState<SelectedShippingAddressType>("");
    const selectedAddressType: SelectedShippingAddressType = pendingAddressType || reduxSelectedAddressType;
    const isChangingAddress =
        isUpdatingCustomer || (!!pendingAddressType && pendingAddressType !== reduxSelectedAddressType);

    // Once Redux catches up to the optimistic choice, drop the override.
    useEffect(() => {
        if (pendingAddressType && reduxSelectedAddressType === pendingAddressType) {
            setPendingAddressType("");
        }
    }, [reduxSelectedAddressType, pendingAddressType]);

    // Keep the "address requires update" flag in sync with the selected saved/billing
    // address, so the inline edit form shows when (and only when) the address is invalid.
    useEffect(() => {
        if ((reduxSelectedAddressType === "saved" || reduxSelectedAddressType === "billing") && address) {
            validateSelectedShippingAddress({ shipTo: address });
        }
    }, [address?.id, reduxSelectedAddressType]);

    useEffect(() => {
        if (addressBeingEdited) {
            setIsEditAddressModalOpen(true);
        }
    }, [addressBeingEdited]);

    const handleAddressTypeSelect = (value: "savedAddress" | "onetime" | "billing") => {
        if (value === "savedAddress") {
            selectAddressClickHandler();
            return;
        }
        // Re-selecting the already active option would rebuild the ship-to and wipe the
        // in-progress form, so ignore clicks on the option that is already selected.
        if (selectedAddressType === value) {
            return;
        }
        if (value === "onetime") {
            setPendingAddressType("onetime");
            markShippingAddressOptionSelected();
            setUseOneTimeAddress({
                useOneTimeAddress: true,
            });
            if (address) {
                setAddress({
                    ...address,
                    properties: {
                        residentialFlag: "",
                        dockDoorRequired: "",
                    },
                });
            }
        } else if (value === "billing") {
            setPendingAddressType("billing");
            markShippingAddressOptionSelected();
            setUseBillingAddress({
                useBillingAddress: true,
            });
        }
    };

    const addressTypeChangeHandler: RadioGroupProps["onChangeHandler"] = (event: React.ChangeEvent<HTMLInputElement>) => {
        handleAddressTypeSelect(event.target.value as "savedAddress" | "onetime" | "billing");
    };

    const handleAddressOptionClick = (value: string) => {
        handleAddressTypeSelect(value as "savedAddress" | "onetime" | "billing");
    };

    const selectAddressClickHandler = () => {
        theme.addressBook?.useOverlay ? setAddressBookOverlayIsOpen({ isOpen: true, isBillTo: false }) : setIsCustomerSelectorModalOpen(true);
    };

    const openAddressModalHandler = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        event.preventDefault();
        event.stopPropagation();
        theme.addressBook?.useOverlay ? setAddressBookOverlayIsOpen({ isOpen: true, isBillTo: false }) : setIsCustomerSelectorModalOpen(true);
    };

    const customerSelectorModalCloseHandler = () => {
        setIsCustomerSelectorModalOpen(false);
    };

    const selectCustomerHandler = (shipTo: ShipToModel) => {
        customerSelectorModalCloseHandler();
        setPendingAddressType("saved");
        markShippingAddressOptionSelected();
        onChange(shipTo, false);
    };

    const createNewAddressHandler = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        event.preventDefault();
        setIsCreateNewAddressModalOpen(true);
    };

    const createNewAddressModalCloseHandler = () => setIsCreateNewAddressModalOpen(false);

    const newShippingAddressFormSubmitHandler = (_: React.FormEvent<HTMLFormElement>, address: ShipToModel) => {
        setPendingAddressType("saved");
        markShippingAddressOptionSelected();
        onChange(address, true);
        createNewAddressModalCloseHandler();
        customerSelectorModalCloseHandler();
        toasterContext.addToast({
            messageType: "success",
            body: translate("Address has been created."),
        });
    };

    const editCustomerHandler = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>, customer: BaseAddressModel) => {
        event.preventDefault();
        setAddressBeingEdited(customer as ShipToModel);
    };

    const editAddressModalCloseHandler = () => {
        setIsEditAddressModalOpen(false);
        setAddressBeingEdited(undefined);
    };

    const editAddressFormSubmitHandler = (_: React.FormEvent<HTMLFormElement>, address: ShipToModel) => {
        setPendingAddressType("saved");
        markShippingAddressOptionSelected();
        onChange(address, true);
        editAddressModalCloseHandler();
        customerSelectorModalCloseHandler();
        toasterContext.addToast({
            messageType: "success",
            body: translate("Address updated."),
        });
    };

    const showSavedAddressOption = !currentUserIsGuest || allowCreateNewShipToAddress || !!newAddress || !!address;

    // Map the derived selection to the RadioGroup's radio values.
    const radioGroupValue = selectedAddressType === "saved" ? "savedAddress" : selectedAddressType;

    return (
        <GridContainer {...styles.container} data-test-selector="checkoutShipping_shippingAddress">
            <CheckoutSectionHeader title={translate("Shipping Address")} />
            <GridItem {...styles.cardContentGridItem}>
                <StyledWrapper {...styles.cardContent}>
                    {(showSavedAddressOption || showUseBillingAddress || oneTimeAddress) && (
                        <RadioGroup {...styles.addressTypeRadioGroup} value={radioGroupValue} onChangeHandler={addressTypeChangeHandler}>
                            {showSavedAddressOption && (
                                <RadioCard
                                    value="savedAddress"
                                    isSelected={selectedAddressType === "saved" && !!address}
                                    onSelect={handleAddressOptionClick}
                                    onClickChild={selectAddressClickHandler}
                                    excludeClickSelectors="input, label, button, a"
                                    className="address-option"
                                >
                                    <StyledWrapper
                                        css={css`
                                            display: flex;
                                            align-items: center;
                                            justify-content: space-between;

                                            ${(theme: any) => breakpointMediaQueries(theme, [
                                                null,
                                                null,
                                                css`
                                                    flex-wrap: wrap;
                                                    column-gap: 8px;
                                                `,
                                            ], "max")}
                                        `}
                                    >
                                        <Radio
                                            value="savedAddress"
                                            onClick={selectAddressClickHandler}
                                            data-test-selector="checkoutShipping_selectSavedAddress"
                                        >
                                            {translate("Ship to Saved Address")}
                                        </Radio>
                                        {selectedAddressType === "saved" && (
                                            <Button
                                                variant="tertiary"
                                                onClick={openAddressModalHandler}
                                                data-test-selector="checkoutShipping_changeAddress"
                                                type="button"
                                                css={css`
                                                    padding: 0;
                                                    color: #87695b;
                                                    font-weight: 600;
                                                    font-size: 16px;
                                                    text-decoration: none;
                                                    background: none;
                                                    border: none;
                                                    cursor: pointer;
                                                    &:hover {
                                                        text-decoration: underline;
                                                        background: none;
                                                        color: #87695b;
                                                    }
                                                `}
                                            >
                                                {address ? translate("Change Address") : translate("Select Address")}
                                            </Button>
                                        )}
                                    </StyledWrapper>
                                    {selectedAddressType === "saved" &&
                                        address &&
                                        (isChangingAddress ? (
                                            <StyledWrapper {...styles.loadingOverlay}>
                                                <LoadingSpinner size={30} />
                                            </StyledWrapper>
                                        ) : (
                                            shippingAddressFormState?.address.id !== currentBillToId && (
                                                <div style={{ marginTop: "10px" }}>
                                                    <AddressInfoDisplay
                                                        {...address}
                                                        state={address?.state?.abbreviation}
                                                        country={address?.country?.abbreviation}
                                                        extendedStyles={styles.addressDisplay}
                                                        addressType={address?.properties.residentialFlag}
                                                        dockdoor={address?.properties.dockDoorRequired}
                                                    />
                                                </div>
                                            )
                                        ))}
                                </RadioCard>
                            )}
                            {showUseBillingAddress && (
                                <RadioCard
                                    value="billing"
                                    isSelected={selectedAddressType === "billing" && !!address}
                                    onSelect={handleAddressOptionClick}
                                    excludeClickSelectors="input, label"
                                    className="address-option"
                                >
                                    <Radio value="billing">{translate("Ship to Billing Address")}</Radio>
                                    {selectedAddressType === "billing" &&
                                        (isChangingAddress ? (
                                            <StyledWrapper {...styles.loadingOverlay}>
                                                <LoadingSpinner size={30} />
                                            </StyledWrapper>
                                        ) : (
                                            address && (
                                                <StyledWrapper {...styles.billingAddressWrapper}>
                                                    <AddressInfoDisplay
                                                        {...address}
                                                        state={address?.state?.abbreviation}
                                                        country={address?.country?.abbreviation}
                                                        extendedStyles={styles.addressDisplay}
                                                        addressType={address?.properties.residentialFlag}
                                                        dockdoor={address?.properties.dockDoorRequired}
                                                    />
                                                </StyledWrapper>
                                            )
                                        ))}
                                </RadioCard>
                            )}
                            {oneTimeAddress && (
                                <RadioCard
                                    value="onetime"
                                    isSelected={selectedAddressType === "onetime"}
                                    isDisabled={currentUserIsGuest}
                                    onSelect={handleAddressOptionClick}
                                    excludeClickSelectors="input, label"
                                    className="address-option"
                                >
                                    <Radio value="onetime" disabled={currentUserIsGuest} data-test-selector="checkoutShipping_useOneTimeAddress">
                                        {translate("Ship to New Address")}
                                    </Radio>
                                    {selectedAddressType === "onetime" && (
                                        <StyledWrapper {...styles.oneTimeAddressWrapper}>
                                            <ShippingAddressForm countries={countries} fieldDisplay={addressFieldDisplayCollection} />
                                        </StyledWrapper>
                                    )}
                                </RadioCard>
                            )}
                        </RadioGroup>
                    )}
                    {(currentUserIsGuest || (isShippingAddressUpdateRequired && (selectedAddressType === "saved" || selectedAddressType === "billing"))) && (
                        <StyledWrapper {...styles.billingAddressWrapper}>
                            <ShippingAddressForm countries={countries} fieldDisplay={addressFieldDisplayCollection} />
                        </StyledWrapper>
                    )}
                    <Modal
                        {...styles.addressBookModal}
                        headline={translate("Addresses")}
                        isOpen={isCustomerSelectorModalOpen}
                        handleClose={() => customerSelectorModalCloseHandler()}
                        data-test-selector="checkoutShipping_addressModal"
                    >
                        <ShipToSelector
                            currentShipTo={useOneTimeAddress && shippingAddressFormState ? undefined : address}
                            currentBillToId={currentBillToId}
                            newAddress={newAddress}
                            onSelect={selectCustomerHandler}
                            onEdit={editCustomerHandler}
                            onCreateNewAddressClick={newAddress && createNewAddressHandler}
                            extendedStyles={styles.shipToSelector}
                        />
                        <StyledWrapper {...styles.addressBookModalButtonsWrapper}>
                            <Button
                                variant="secondary"
                                // {...styles.addressBookModalCancelButton}
                                onClick={() => customerSelectorModalCloseHandler()}
                                data-test-selector="addressModal_cancel"
                                type="button"
                            >
                                {translate("Cancel")}
                            </Button>
                        </StyledWrapper>
                    </Modal>
                    {newAddress && (
                        <CreateNewAddressModal
                            newAddress={newAddress}
                            isModalOpen={isCreateNewAddressModalOpen}
                            onModalClose={createNewAddressModalCloseHandler}
                            onFormSubmit={newShippingAddressFormSubmitHandler}
                        />
                    )}
                    {addressBeingEdited && (
                        <EditExistingAddressModal
                            newAddress={addressBeingEdited}
                            isModalOpen={isEditAddressModalOpen}
                            onModalClose={editAddressModalCloseHandler}
                            onFormSubmit={editAddressFormSubmitHandler}
                        />
                    )}
                </StyledWrapper>
            </GridItem>
        </GridContainer>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withTheme(ShippingAddress));
